import { useEffect, useMemo, useState } from 'react';
import { ImagePreviewGrid } from '../../../components/media/ImagePreviewGrid';
import { useAuthStore } from '../../../store/auth.store';
import { hasAdminPermission } from '../../../lib/adminPermissions';
import { buildEvidenceThumbnailUrl } from '../../../lib/upload/cosThumbnail';
import type {
  MessageEvidenceDTO,
  MessageReviewDetailDTO,
} from '../types/assignments.types';
import { MessageEventTimeline } from './MessageEventTimeline';
import { MessageRiskReasons } from './MessageRiskReasons';
import { messageReviewFailureLabel, messageReviewReasonLabel } from '../utils/messageReviewLabels';

type MessageReviewCardProps = {
  detail: MessageReviewDetailDTO | null;
  loading?: boolean;
  error?: string | null;
  onApprove: (note: string, auditReason?: string) => void;
  onReject: (note: string, auditReason?: string) => void;
  onRetry: (auditReason: string) => void;
  onRefreshImages?: () => void;
  onReplace: (newItemId: number, auditReason: string) => void;
  actionLoading?: boolean;
  reviewing?: boolean;
};

function formatDateTime(iso?: string): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
}

function statusLabel(value?: string): string {
  switch (value) {
    case 'assigned':
      return '已领取';
    case 'submitted':
      return '待审核';
    case 'approved':
      return '已通过';
    case 'rejected':
      return '已拒绝';
    case 'settled':
      return '已结算';
    case 'sent':
      return '已发送';
    case 'reviewed':
      return '已审核';
    case 'finalized':
      return '已完成发送';
    case 'reused':
      return '复用截图';
    case 'sealed':
      return '已封存';
    case 'conflict':
      return '截图冲突';
    case 'unreadable':
      return '不可读';
    case 'pass_candidate':
      return '通过建议';
    case 'fail_candidate':
      return '拒绝建议';
    case 'manual_required':
      return '人工复核';
    default:
      return value || '-';
  }
}

function getSummaryField(summary: Record<string, unknown> | null | undefined, keys: string[]): string {
  if (!summary) return '-';
  for (const key of keys) {
    const value = summary[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return '-';
}

export function MessageReviewCard({
  detail,
  loading,
  error,
  onApprove,
  onReject,
  onRetry,
  onReplace,
  onRefreshImages,
  actionLoading,
  reviewing,
}: MessageReviewCardProps) {
  const user = useAuthStore((s) => s.user);
  const canReview = hasAdminPermission(user, 'message_review_admin');
  const canCompliance = hasAdminPermission(user, 'message_compliance_admin');
  const [approveReason, setApproveReason] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [retryReason, setRetryReason] = useState('');
  const [replaceReason, setReplaceReason] = useState('');
  const [newItemId, setNewItemId] = useState('');
  const [selectedEvidence, setSelectedEvidence] = useState<MessageEvidenceDTO | null>(null);
  const previewOriginalUrl = selectedEvidence?.url;
  const previewUrl = previewOriginalUrl ? buildEvidenceThumbnailUrl(previewOriginalUrl) : null;
  const previewError = Boolean(selectedEvidence && !previewOriginalUrl);
  const retryPreview = onRefreshImages;
  const recommendationData = detail?.recommendation && typeof detail.recommendation === 'object'
    ? detail.recommendation
    : null;
  const recommendation = recommendationData?.status
    ?? (typeof detail?.recommendation === 'string' ? detail.recommendation : undefined)
    ?? 'manual_required';
  const reviewFailure = messageReviewFailureLabel(detail?.review_job, detail?.risk?.redacted_summary as Record<string, unknown> | null | undefined);
  const riskCodes = detail?.risk?.reason_codes ?? [];
  const hasHardRisk = riskCodes.length > 0 && riskCodes.some((code) => ['sent_telemetry_missing', 'attestation_invalid', 'challenge_invalid', 'launch_token_invalid', 'kill_switch_enabled', 'compliance_go_no_go_missing'].includes(code));
  const canReplaceTarget = canCompliance && recommendation === 'fail_candidate' && !hasHardRisk;
  const hasReviewJob = Boolean(detail?.review_job?.id);
  const canRetry = canReview && hasReviewJob && (detail?.review_job?.status === 'failed' || detail?.review_job?.status === 'manual_required' || detail?.review_job?.status === 'succeeded');

  const timeline = useMemo(() => detail?.session_timeline ?? [], [detail?.session_timeline]);
  const evidence = useMemo(() => detail?.evidence ?? [], [detail?.evidence]);
  const visibleEvidence = useMemo(() => {
    const currentSessionID = detail?.review_job?.session_id || detail?.message_target?.session_id;
    const scopedEvidence = currentSessionID
      ? evidence.filter((item) => item.session_id === currentSessionID)
      : [];
    const candidates = scopedEvidence.length > 0 ? scopedEvidence : evidence;
    return [...candidates].sort((left, right) => {
      if (left.position_no !== right.position_no) return left.position_no - right.position_no;
      return right.id - left.id;
    });
  }, [detail?.message_target?.session_id, detail?.review_job?.session_id, evidence]);

  useEffect(() => {
    if (visibleEvidence.length === 0) {
      setSelectedEvidence(null);
      return;
    }
    setSelectedEvidence((current) => {
      if (current) {
        const updated = visibleEvidence.find((item) => item.id === current.id);
        if (updated) return updated;
      }
      return visibleEvidence[0];
    });
  }, [visibleEvidence]);

  if (loading) {
    return (
      <section className="space-y-3">
        <h3 className="text-base font-medium text-gray-900">短信证据审核</h3>
        <p className="text-sm text-gray-400">正在加载...</p>
      </section>
    );
  }

  if (error || !detail) {
    return (
      <section className="space-y-3">
        <h3 className="text-base font-medium text-gray-900">短信证据审核</h3>
        <p className="text-sm text-red-500">{error ?? '暂无短信证据'}</p>
      </section>
    );
  }

  const item = detail.item;
  const target = detail.message_target;
  const recommendationText = statusLabel(recommendation);
  const canReviewEvidence = canReview && item.status === 'submitted' && hasReviewJob && visibleEvidence.length > 0;

  const handleApprove = () => {
    const auditReason = approveReason.trim();
    onApprove('通过', auditReason || undefined);
  };

  const handleReject = () => {
    const auditReason = rejectReason.trim();
    onReject('拒绝', auditReason || undefined);
  };

  return (
    <section className="space-y-4 rounded-md border border-gray-200 bg-white px-4 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h3 className="text-base font-medium text-gray-900">短信证据审核</h3>
          <p className="text-sm text-gray-500">证据建议，不代表送达证明</p>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-medium ${
          recommendation === 'pass_candidate'
            ? 'bg-green-50 text-green-700'
            : recommendation === 'fail_candidate'
              ? 'bg-red-50 text-red-700'
              : 'bg-amber-50 text-amber-700'
        }`}>
          {recommendationText}
        </span>
      </div>

      {reviewFailure && (
        <div role="alert" className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="font-medium">自动审核异常</p>
          <p className="mt-1">{reviewFailure}</p>
          <p className="mt-1">已尝试 {detail.review_job?.attempt_count ?? 0} / {detail.review_job?.max_attempts ?? '-'} 次。截图识别未完成，暂不能据此判断内容是否符合要求。</p>
        </div>
      )}

      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-900">任务要求与提交截图</h4>
        <div className="grid gap-5 xl:grid-cols-2">
          <div className="space-y-1 xl:border-r xl:border-gray-200 xl:pr-5">
            <CardField label="任务目标号码" value={target?.target_account || '-'} />
            <CardField label="任务要求正文" value={target?.target_content || '-'} />
          </div>
          <div className="space-y-3">
            <h5 className="text-xs font-medium text-gray-500">用户提交截图</h5>
            {previewUrl ? (
              <ImagePreviewGrid
                images={[{ src: previewUrl, fullSrc: previewOriginalUrl ?? undefined }]}
                onRetryImage={retryPreview}
                altPrefix="用户提交短信截图"
                gridClassName="grid grid-cols-1"
                thumbClassName="max-h-[560px]"
              />
            ) : (
              <div className="flex min-h-40 items-center justify-center text-sm text-gray-400">
                {previewError ? <button type="button" onClick={retryPreview} className="text-primary">加载失败，点击重试</button> : visibleEvidence.length > 0 ? '截图加载中' : '暂无截图'}
              </div>
            )}
          </div>
        </div>
        {visibleEvidence.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {visibleEvidence.map((itemEvidence) => (
              <button
                key={itemEvidence.id}
                type="button"
                onClick={() => setSelectedEvidence(itemEvidence)}
                className={`w-24 overflow-hidden rounded-md border text-left transition-colors ${selectedEvidence?.id === itemEvidence.id ? 'border-primary ring-2 ring-primary/20' : 'border-gray-200 hover:border-gray-300'}`}
              >
                <EvidenceThumbnail evidence={itemEvidence} />
                <div className="px-2 py-1.5">
                  <p className="text-xs font-medium text-gray-900">第 {itemEvidence.position_no} 张</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <CardField label="任务状态" value={statusLabel(item.status)} />
        <CardField label="发送状态" value={statusLabel(target?.session_status || target?.item_status)} />
        <CardField label="提交时间" value={formatDateTime(item.submitted_at)} />
        <CardField label="App 版本" value={item.latest_app_version || '-'} />
        <CardField label="审核权限" value={canReview ? '运营自行审核' : '缺少短信审核权限'} />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-900">审核结论</h4>
          <div className="rounded-md border border-gray-200 px-4">
            <CardField label="建议" value={recommendationText} />
            <CardField label="Provider" value={recommendationData?.provider || '-'} />
            <CardField label="Model" value={recommendationData?.model || '-'} />
            <CardField label="Reasoning" value={recommendationData?.reasoning_effort || '-'} />
            <CardField label="Config" value={recommendationData?.config_version || '-'} />
            <CardField label="Prompt" value={recommendationData?.prompt_version || '-'} />
            <CardField label="Request ID" value={recommendationData?.provider_request_id || '-'} />
            <h5 className="border-t border-gray-100 pt-3 text-xs font-medium text-gray-500">图片识别结果</h5>
            <CardField label="收件人摘要" value={getSummaryField(detail.risk?.redacted_summary as Record<string, unknown> | null | undefined, ['recipient_summary', 'recipient_mask', 'recipient', 'extracted_recipient'])} />
            <CardField label="正文摘要" value={getSummaryField(detail.risk?.redacted_summary as Record<string, unknown> | null | undefined, ['body_summary', 'body_mask', 'body', 'extracted_body'])} />
          </div>
        </div>
        <MessageRiskReasons
          serviceFailure={Boolean(reviewFailure)}
          reasonCodes={detail.risk?.reason_codes}
          recipientMatches={detail.risk?.recipient_matches}
          bodyMatches={detail.risk?.body_matches}
          messagesScreen={detail.risk?.messages_screen}
          blueIMessageDetected={detail.risk?.blue_imessage_detected}
          redactedSummary={detail.risk?.redacted_summary as Record<string, unknown> | null | undefined}
        />
      </div>

      <MessageEventTimeline records={timeline} />

      <div className="rounded-md border border-gray-200 px-4 py-3">
        <h4 className="text-sm font-medium text-gray-900">App Attest 验证</h4>
        <div className="mt-2 space-y-2 text-sm text-gray-700">
          {(detail.app_attest?.keys ?? []).length === 0 && (detail.app_attest?.challenges ?? []).length === 0 ? (
            <p className="text-gray-400">暂无 App Attest 记录</p>
          ) : (
            <>
              {(detail.app_attest?.keys ?? []).map((key) => (
                <div key={key.key_id} className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-gray-900">Key {key.key_id}</span>
                  <span>{key.status}</span>
                  <span>counter {key.counter}</span>
                  <span>{key.last_used_at ? formatDateTime(key.last_used_at) : '-'}</span>
                </div>
              ))}
              {(detail.app_attest?.challenges ?? []).map((challenge) => (
                <div key={challenge.id} className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                  <span>{challenge.purpose}</span>
                  <span>{formatDateTime(challenge.created_at)}</span>
                  <span>{formatDateTime(challenge.expires_at)}</span>
                  <span>{challenge.used_at ? formatDateTime(challenge.used_at) : '未使用'}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {canReviewEvidence && (
        <div className="space-y-3 rounded-md border border-gray-200 px-4 py-3">
          <h4 className="text-sm font-medium text-gray-900">审核操作</h4>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="space-y-1">
              <span className="text-xs text-gray-500">批准备注</span>
              <textarea value={approveReason} onChange={(e) => setApproveReason(e.target.value)} rows={3} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="可选" />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-gray-500">拒绝原因</span>
              <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="必填建议填写原因" />
            </label>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleApprove}
              disabled={actionLoading || reviewing || !canReview}
              className="rounded-md bg-green-600 px-4 py-2 text-sm text-white disabled:opacity-40"
            >
              普通批准
            </button>
            <button
              type="button"
              onClick={handleReject}
              disabled={actionLoading || reviewing || !canReview}
              className="rounded-md border border-red-300 px-4 py-2 text-sm text-red-700 disabled:opacity-40"
            >
              拒绝
            </button>
            {canRetry && (
              <button
                type="button"
                onClick={() => onRetry(retryReason.trim() || 'retry message review')}
                disabled={actionLoading || reviewing}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 disabled:opacity-40"
              >
                重试审核
              </button>
            )}
          </div>
          <label className="space-y-1 block">
            <span className="text-xs text-gray-500">重试原因</span>
            <textarea value={retryReason} onChange={(e) => setRetryReason(e.target.value)} rows={2} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="用于重试任务" />
          </label>
        </div>
      )}

      {canReplaceTarget && (
        <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
          <h4 className="text-sm font-medium text-amber-900">更换目标</h4>
          <div className="grid gap-3 md:grid-cols-[1fr_2fr]">
            <input
              value={newItemId}
              onChange={(e) => setNewItemId(e.target.value)}
              inputMode="numeric"
              placeholder="新 item ID"
              className="rounded-md border border-amber-200 bg-white px-3 py-2 text-sm"
            />
            <input
              value={replaceReason}
              onChange={(e) => setReplaceReason(e.target.value)}
              placeholder="更换原因"
              className="rounded-md border border-amber-200 bg-white px-3 py-2 text-sm"
            />
          </div>
          <button
            type="button"
            onClick={() => onReplace(Number(newItemId), replaceReason.trim())}
            disabled={actionLoading || reviewing || !newItemId.trim() || !replaceReason.trim()}
            className="rounded-md bg-amber-600 px-4 py-2 text-sm text-white disabled:opacity-40"
          >
            更换目标
          </button>
        </div>
      )}

      {selectedEvidence && (
        <p className="text-xs text-gray-500">
          当前预览：第 {selectedEvidence.position_no} 张，{statusLabel(selectedEvidence.status)}，{messageReviewReasonLabel(selectedEvidence.reason_code)}
        </p>
      )}
    </section>
  );
}

function EvidenceThumbnail({ evidence }: { evidence: MessageEvidenceDTO }) {
  const src = evidence.url ? buildEvidenceThumbnailUrl(evidence.url) : null;
  const error = !src;

  return (
    <div className="overflow-hidden rounded-md border border-gray-200">
      <div className="aspect-[9/16] bg-gray-50">
        {src ? (
          <img src={src} alt={`证据 ${evidence.position_no}`} className="h-full w-full object-contain" />
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-gray-400">{error ? '加载失败' : '加载中'}</div>
        )}
      </div>
    </div>
  );
}

function CardField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <span className="w-24 shrink-0 text-xs text-gray-500">{label}</span>
      <span className="min-w-0 flex-1 whitespace-pre-wrap break-words text-sm text-gray-900">{value}</span>
    </div>
  );
}
